import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Search, X } from "lucide-react";
import {
  pdfBase64ToObjectUrl,
  timetableDataQualityApi,
  type DataQualityCategory,
  type DataQualityItem,
  type TeacherContextEntry,
  type TeacherContextPdf,
  type TeacherContextResponse,
} from "@/services/api/timetableDataQuality";
import { ApiErrorResponse } from "@/services/api/client";
import { extractErrorMessage } from "@/utils/errorHandler";
import {
  formatPeriodLabel,
  groupEntriesByPeriod,
  isPeriodCurrent,
} from "@/utils/timetableGrouping";
import {
  DAY_INT_TO_LABEL,
  entryMatchesGroup,
  occupiedDays,
  occupiedSlotLabels,
  orderedSlotLabels,
  slotSpanLabels,
  toGridEntry,
  weekDaysForEntries,
} from "@/utils/teacherContextGrid";
import { TimetableGridCSS } from "@/components/timetable-import/TimetableGridCSS";
import { PdfTimetableModal } from "@/components/dataquality/PdfTimetableModal";
import { useEntryFieldRebind } from "@/components/dataquality/useEntryFieldRebind";
import {
  EvidencePdfPane,
  type PdfViewPersist,
} from "@/components/dataquality/EvidencePdfPane";
import { SystemFitBar, ZoomSlider } from "@/components/dataquality/ZoomSlider";
import { FittedScrollPane } from "@/components/dataquality/FittedScrollPane";
import {
  DEFAULT_PERSON_VIEW,
  flushPersonViews,
  queuePersonView,
  resolvePersonView,
  writePersonView,
  type PersonView,
} from "@/components/dataquality/personViewMemory";

const CATEGORY_LABELS: Record<DataQualityCategory, string> = {
  subject: "Fach",
  room: "Raum",
  klass: "Klasse",
};

interface CatalogEvidencePanelProps {
  item: DataQualityItem;
  batchId: string | null;
  findingsReady: boolean;
  onClose: () => void;
  onTeacherReviewed?: (teacherId: string, confirmed: boolean) => void;
}

function slotLine(
  entry: TeacherContextEntry,
  category: DataQualityCategory,
  includePeriod: boolean
): string {
  const day =
    entry.day_of_week != null
      ? (DAY_INT_TO_LABEL[entry.day_of_week] ?? "ohne Tag")
      : "ohne Tag";
  const lesson =
    entry.lesson_number != null
      ? `L${entry.lesson_number}`
      : entry.time_slot_name || "ohne Slot";
  const sys =
    category === "klass"
      ? `Klasse ${entry.klass_name?.trim() || "—"}`
      : category === "subject"
        ? `Fach ${entry.subject_name?.trim() || "—"}`
        : `Raum ${entry.room_name?.trim() || "—"}`;
  const prefix = includePeriod
    ? `${formatPeriodLabel(entry.valid_from, entry.valid_to)} · `
    : "";
  const pdf = entry.pdf_raw_text?.trim();
  const pdfPart = pdf ? ` · PDF: ${pdf}` : "";
  return `${prefix}${day} ${lesson} · System: ${sys}${pdfPart}`;
}

type TeacherReviewFilter = "open" | "confirmed" | "all";

export function CatalogEvidencePanel({
  item,
  batchId,
  findingsReady,
  onClose,
  onTeacherReviewed,
}: CatalogEvidencePanelProps) {
  const teachers = item.teachers;
  const [selectedId, setSelectedId] = useState<string | null>(
    teachers.find((t) => !t.manually_confirmed)?.id ?? teachers[0]?.id ?? null
  );
  const [query, setQuery] = useState("");
  const [reviewFilter, setReviewFilter] = useState<TeacherReviewFilter>("open");
  const [confirmedById, setConfirmedById] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        teachers.map((t) => [t.id, Boolean(t.manually_confirmed)])
      )
  );
  const [reviewError, setReviewError] = useState<string | null>(null);
  const confirmedRef = useRef<Record<string, boolean>>({});
  const latestIntent = useRef<Record<string, boolean>>({});
  const reviewChains = useRef<Map<string, Promise<void>>>(new Map());
  const reviewQueued = useRef<Record<string, boolean>>({});
  const alive = useRef(true);
  const [context, setContext] = useState<TeacherContextResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfMeta, setPdfMeta] = useState<TeacherContextPdf | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLarger, setPdfLarger] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [viewOverrides, setViewOverrides] = useState<
    Record<string, PersonView>
  >({});
  const pdfUrlRef = useRef<string | null>(null);
  const pdfOwnerRef = useRef(selectedId);
  if (pdfOwnerRef.current !== selectedId) {
    pdfOwnerRef.current = selectedId;
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfUrl(null);
    setPdfMeta(null);
    setPdfLarger(false);
    setPdfLoading(true);
  }
  const closeRef = useRef<HTMLButtonElement>(null);
  const rebind = useEntryFieldRebind(() => setReloadKey((n) => n + 1));

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  useEffect(
    () => () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
      flushPersonViews();
    },
    []
  );

  confirmedRef.current = confirmedById;
  useEffect(() => {
    setConfirmedById((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const teacher of teachers) {
        if (
          reviewQueued.current[teacher.id] ||
          latestIntent.current[teacher.id] !== undefined
        ) {
          continue;
        }
        const flag = Boolean(teacher.manually_confirmed);
        if (Boolean(next[teacher.id]) !== flag) {
          next[teacher.id] = flag;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [teachers]);
  const isConfirmed = (id: string) => Boolean(confirmedById[id]);
  const storedView = selectedId
    ? resolvePersonView(selectedId)
    : DEFAULT_PERSON_VIEW;
  const personView: PersonView = {
    ...storedView,
    ...viewOverrides[selectedId ?? ""],
    systemScrollLeft: storedView.systemScrollLeft,
    systemScrollTop: storedView.systemScrollTop,
    pdfScrollLeft: storedView.pdfScrollLeft,
    pdfScrollTop: storedView.pdfScrollTop,
  };
  const pdfView: PdfViewPersist = {
    scale: personView.pdfScale,
    scrollLeft: personView.pdfScrollLeft,
    scrollTop: personView.pdfScrollTop,
  };
  const updatePersonView = (patch: Partial<PersonView>) => {
    if (!selectedId) return;
    const next = writePersonView(selectedId, patch);
    setViewOverrides((prev) => ({ ...prev, [selectedId]: next }));
  };
  const queueScroll = (patch: Partial<PersonView>) => {
    if (!selectedId) return;
    queuePersonView(selectedId, patch);
  };
  const selected = teachers.find((t) => t.id === selectedId) ?? null;
  const selectedConfirmed = selectedId ? isConfirmed(selectedId) : false;
  const showSearch = teachers.length >= 12;
  const filteredTeachers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return teachers.filter((t) => {
      if (reviewFilter === "open" && isConfirmed(t.id)) return false;
      if (reviewFilter === "confirmed" && !isConfirmed(t.id)) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        (t.code ?? "").toLowerCase().includes(q)
      );
    });
  }, [teachers, query, reviewFilter, confirmedById]);

  const queueTeacherReview = (teacherId: string, confirmed: boolean) => {
    latestIntent.current[teacherId] = confirmed;
    if (reviewQueued.current[teacherId]) return;
    reviewQueued.current[teacherId] = true;
    const category = item.category;
    const value = item.value;
    const isNullValue = item.is_null_value;
    const prev = reviewChains.current.get(teacherId) ?? Promise.resolve();
    const run = prev
      .catch(() => undefined)
      .then(async () => {
        try {
          for (;;) {
            const intent = latestIntent.current[teacherId];
            if (intent === undefined) return;
            if (alive.current) setReviewError(null);
            try {
              await timetableDataQualityApi.setTeacherReview({
                category,
                value,
                is_null_value: isNullValue,
                teacher_id: teacherId,
                confirmed: intent,
              });
            } catch (err) {
              if (latestIntent.current[teacherId] !== intent) continue;
              delete latestIntent.current[teacherId];
              if (alive.current) {
                setReviewError(
                  extractErrorMessage(err, {
                    loadError: "Bestätigung fehlgeschlagen",
                  })
                );
              }
              return;
            }
            if (latestIntent.current[teacherId] !== intent) continue;
            const updated = { ...confirmedRef.current, [teacherId]: intent };
            confirmedRef.current = updated;
            onTeacherReviewed?.(teacherId, intent);
            if (!alive.current) return;
            setConfirmedById(updated);
            const stillOpen = teachers.some((t) => !updated[t.id]);
            if (intent && !stillOpen) {
              onClose();
              return;
            }
            if (intent && reviewFilter === "open") {
              const following = teachers.find((t) => !updated[t.id]);
              if (following) setSelectedId(following.id);
            }
            return;
          }
        } finally {
          reviewQueued.current[teacherId] = false;
          const pending = latestIntent.current[teacherId];
          const applied =
            Object.prototype.hasOwnProperty.call(
              confirmedRef.current,
              teacherId
            ) && confirmedRef.current[teacherId] === pending;
          if (pending !== undefined && !applied) {
            queueTeacherReview(teacherId, pending);
          }
        }
      });
    reviewChains.current.set(teacherId, run);
  };

  const toggleTeacher = (teacherId: string) => {
    const current = Object.prototype.hasOwnProperty.call(
      latestIntent.current,
      teacherId
    )
      ? latestIntent.current[teacherId]
      : Boolean(confirmedRef.current[teacherId]);
    queueTeacherReview(teacherId, !current);
  };

  useEffect(() => {
    if (!findingsReady || !selectedId) return;
    let cancelled = false;
    setError(null);
    setPdfError(null);
    setContext(null);
    setPdfMeta(null);
    setPdfLarger(false);
    setPdfLoading(true);
    void (async () => {
      try {
        const ctx = await timetableDataQualityApi.getTeacherContext(selectedId);
        if (cancelled) return;
        setContext(ctx);
        let pdf = ctx.pdf;
        if (!pdf && batchId) {
          const pdfCtx = await timetableDataQualityApi.getTeacherContext(
            selectedId,
            batchId
          );
          if (cancelled) return;
          if (pdfCtx.pdf) pdf = pdfCtx.pdf;
        }
        setPdfMeta(pdf);
        if (!pdf?.page) {
          const prev = pdfUrlRef.current;
          pdfUrlRef.current = null;
          setPdfUrl(null);
          if (prev) URL.revokeObjectURL(prev);
          setPdfLoading(false);
          return;
        }
        try {
          const page = await timetableDataQualityApi.getPdfPage(
            pdf.file_id,
            pdf.page
          );
          if (cancelled) return;
          if (page.page_number !== pdf.page) {
            setPdfLoading(false);
            return;
          }
          const url = pdfBase64ToObjectUrl(page.pdf_base64);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          const prev = pdfUrlRef.current;
          pdfUrlRef.current = url;
          setPdfUrl(url);
          if (prev) URL.revokeObjectURL(prev);
          setPdfLoading(false);
        } catch (err) {
          if (cancelled) return;
          setPdfLoading(false);
          if (err instanceof ApiErrorResponse && err.statusCode === 422) return;
          setPdfError(
            extractErrorMessage(err, {
              loadError: "PDF konnte nicht geladen werden",
            })
          );
        }
      } catch (err) {
        if (cancelled) return;
        setPdfLoading(false);
        setError(
          extractErrorMessage(err, {
            loadError: "Stundenplan konnte nicht geladen werden",
          })
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, batchId, findingsReady, reloadKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (rebind.isOpen) return;
      if (pdfLarger) {
        setPdfLarger(false);
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pdfLarger, rebind.isOpen, onClose]);

  const matches = useMemo(() => {
    if (!context) return [];
    return context.entries.filter((e) =>
      entryMatchesGroup(e, item.category, item.is_null_value, item.value)
    );
  }, [context, item.category, item.is_null_value, item.value]);

  const periods = useMemo(() => {
    if (!context) return [];
    return groupEntriesByPeriod(context.entries);
  }, [context]);

  const valueLabel = item.is_null_value
    ? `(ohne ${CATEGORY_LABELS[item.category]})`
    : (item.value ?? "—");
  const catalogCount = selected?.entry_count ?? 0;
  const includePeriod = periods.length > 1;
  const dayNumbers = context?.entries.map((entry) => entry.day_of_week) ?? [];
  const gridDays =
    personView.systemFit === "fixed-width"
      ? occupiedDays(dayNumbers)
      : weekDaysForEntries(dayNumbers);
  const gridSlots =
    personView.systemFit === "height"
      ? occupiedSlotLabels(context?.entries ?? [], context?.slots)
      : personView.systemFit === "fit"
        ? slotSpanLabels(context?.entries ?? [], context?.slots)
        : orderedSlotLabels(context?.entries ?? [], context?.slots);
  const showGridLoading =
    !findingsReady || (Boolean(selectedId) && !context && !error);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1"
      onClick={(e) => {
        if (e.target === e.currentTarget && !pdfLarger && !rebind.isOpen)
          onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="catalog-evidence-title"
        className="bg-white rounded-lg w-[98vw] h-[98vh] max-w-none flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-gray-100 flex items-start justify-between shrink-0">
          <div>
            <h3 id="catalog-evidence-title" className="text-lg font-semibold">
              {CATEGORY_LABELS[item.category]} · {valueLabel}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {item.entry_count} Einträge · {item.teacher_count} Lehrkräfte ·
              Badge klicken zum Bearbeiten
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Schliessen"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        {!batchId && findingsReady && (
          <p className="mx-4 mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Kein Import-Batch — aktueller Systemstand.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)_minmax(0,1fr)] min-h-0 flex-1 overflow-hidden">
          <div className="border-r border-gray-100 overflow-auto min-h-0 max-h-[22vh] md:max-h-none flex flex-col">
            <div className="p-2 border-b border-gray-100 shrink-0 space-y-2">
              <div className="flex gap-1">
                {(
                  [
                    ["open", "Offen"],
                    ["confirmed", "Bestätigt"],
                    ["all", "Alle"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setReviewFilter(id)}
                    className={`flex-1 px-1.5 py-1 text-[11px] rounded border ${
                      reviewFilter === id
                        ? "bg-gray-900 text-white border-gray-900"
                        : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {showSearch && (
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Lehrkraft…"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                </div>
              )}
            </div>
            {teachers.length === 0 ? (
              <p className="p-3 text-sm text-gray-500">
                Keine Lehrkräfte in dieser Gruppe.
              </p>
            ) : filteredTeachers.length === 0 ? (
              <p className="p-3 text-sm text-gray-500">
                {reviewFilter === "open"
                  ? "Keine offenen Lehrkräfte. Filter «Bestätigt» oder «Alle»."
                  : "Keine Lehrkräfte in diesem Filter."}
              </p>
            ) : (
              filteredTeachers.map((t) => {
                const confirmed = isConfirmed(t.id);
                return (
                  <div
                    key={t.id}
                    className={`w-full flex items-center gap-2 px-2 py-2 text-left text-sm border-b border-gray-100 ${
                      t.id === selectedId ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={confirmed}
                      aria-label={`${t.name} manuell bestätigt`}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleTeacher(t.id);
                      }}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <button
                      type="button"
                      onClick={() => setSelectedId(t.id)}
                      aria-current={t.id === selectedId ? "true" : undefined}
                      className="flex-1 min-w-0 flex justify-between gap-2 text-left"
                    >
                      <span className="truncate">
                        <span className="font-medium">{t.name}</span>
                        {t.code && (
                          <span className="ml-1 font-mono text-xs text-gray-500">
                            {t.code}
                          </span>
                        )}
                      </span>
                      <span
                        className={`font-semibold tabular-nums ${confirmed ? "text-green-700" : "text-red-700"}`}
                      >
                        {t.entry_count ?? 0}
                      </span>
                    </button>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex h-full min-h-0 flex-col overflow-hidden p-2">
            <div className="flex items-center justify-between gap-2 mb-1 shrink-0">
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                System-Stundenplan
              </h5>
              <div className="flex flex-col items-end gap-1">
                <SystemFitBar
                  value={personView.systemFit}
                  onChange={(systemFit) => updatePersonView({ systemFit })}
                />
                <ZoomSlider
                  value={personView.systemZoom}
                  onChange={(systemZoom) => updatePersonView({ systemZoom })}
                  label="System-Zoom"
                />
              </div>
            </div>
            {selected && (
              <button
                type="button"
                onClick={() => toggleTeacher(selected.id)}
                className={`mb-2 shrink-0 w-full px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${
                  selectedConfirmed
                    ? "bg-green-100 text-green-800 border border-green-300 hover:bg-green-50"
                    : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                {selectedConfirmed
                  ? "Manuell bestätigt — Klick hebt auf"
                  : "Manuell bestätigt"}
              </button>
            )}
            {reviewError && (
              <p className="text-sm text-red-600 mb-2">{reviewError}</p>
            )}
            {rebind.loadError && (
              <p className="text-sm text-red-600 mb-2">{rebind.loadError}</p>
            )}
            {showGridLoading ? (
              <p className="text-sm text-gray-500 py-8 text-center">
                Lade Stundenplan…
              </p>
            ) : error ? (
              <div className="flex items-center text-red-600 text-sm py-8">
                <AlertCircle className="w-4 h-4 mr-2" />
                {error}
              </div>
            ) : !selectedId ? (
              <p className="text-sm text-gray-500 py-8 text-center">
                Keine Lehrkräfte in dieser Gruppe.
              </p>
            ) : periods.length === 0 ? (
              <p className="text-sm text-gray-500 py-8">
                Kein System-Stundenplan für diese Lehrkraft
                {catalogCount > 0 ? ` (Katalog: ${catalogCount}).` : "."}
              </p>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col">
                <FittedScrollPane
                  resetKey={selectedId ?? "none"}
                  zoom={personView.systemZoom}
                  fitMode={personView.systemFit}
                  scrollLeft={personView.systemScrollLeft}
                  scrollTop={personView.systemScrollTop}
                  onScroll={(systemScrollLeft, systemScrollTop) => {
                    queueScroll({ systemScrollLeft, systemScrollTop });
                  }}
                  onWheelZoom={(systemZoom) => updatePersonView({ systemZoom })}
                  className="flex-1"
                >
                  <div className="w-max space-y-4">
                    {periods.map((period, idx) => {
                      const highlighted = new Set(
                        period.entries
                          .map((entry, i) =>
                            entryMatchesGroup(
                              entry,
                              item.category,
                              item.is_null_value,
                              item.value
                            )
                              ? i
                              : -1
                          )
                          .filter((i) => i >= 0)
                      );
                      const current = isPeriodCurrent(
                        period.validFrom,
                        period.validTo
                      );
                      return (
                        <div
                          key={idx}
                          className="w-max overflow-visible border border-gray-200 rounded-lg"
                        >
                          <div
                            className={`px-3 py-1.5 text-xs ${current ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-600"}`}
                          >
                            {formatPeriodLabel(
                              period.validFrom,
                              period.validTo
                            )}
                            {current && (
                              <span className="ml-2 bg-green-100 px-1.5 py-0.5 rounded">
                                Aktuell
                              </span>
                            )}
                          </div>
                          <div className="p-2">
                            <TimetableGridCSS
                              entries={period.entries.map((entry) => {
                                const g = toGridEntry(entry);
                                if (
                                  selectedConfirmed &&
                                  entryMatchesGroup(
                                    entry,
                                    item.category,
                                    item.is_null_value,
                                    item.value
                                  )
                                ) {
                                  g.ampel = "green";
                                }
                                return g;
                              })}
                              onCellClick={(
                                _day,
                                _slot,
                                indices,
                                event,
                                field
                              ) => {
                                const entry =
                                  indices[0] !== undefined
                                    ? period.entries[indices[0]]
                                    : null;
                                if (!entry || !field) return;
                                void rebind.open(entry.id, field, event);
                              }}
                              onCellDoubleClick={() => {}}
                              highlightedCell={null}
                              highlightedEntryIndices={highlighted}
                              fitToView={false}
                              intrinsic
                              fixedDays={
                                gridDays.length > 0 ? gridDays : undefined
                              }
                              fixedTimeSlots={
                                gridSlots.length > 0 ? gridSlots : undefined
                              }
                              includeEmptyEntries
                              dimUnhighlighted={
                                matches.length > 0 && !selectedConfirmed
                              }
                              confirmedCategory={
                                selectedConfirmed ? item.category : null
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </FittedScrollPane>
                <div className="mt-2 max-h-24 shrink-0 overflow-auto text-sm text-gray-700">
                  <strong>
                    {matches.length} Einträge dieser Gruppe bei{" "}
                    {selected?.name ?? ""}
                    {catalogCount > matches.length
                      ? ` · Katalog: ${catalogCount}`
                      : ""}
                  </strong>
                  <ul className="mt-1 space-y-0.5">
                    {matches.map((entry) => (
                      <li key={entry.id}>
                        {slotLine(entry, item.category, includePeriod)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col min-h-0 p-2 border-t md:border-t-0 md:border-l border-gray-100">
            <div className="flex items-center justify-between gap-2 mb-1 shrink-0">
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                PDF-Stundenplan dieser Person
              </h5>
              <div className="flex items-center gap-2">
                <ZoomSlider
                  value={pdfView.scale}
                  onChange={(pdfScale) => updatePersonView({ pdfScale })}
                  label="PDF-Zoom"
                />
                {pdfUrl && (
                  <button
                    type="button"
                    onClick={() => setPdfLarger(true)}
                    className="text-xs font-medium text-blue-700 hover:underline"
                  >
                    PDF grösser
                  </button>
                )}
              </div>
            </div>
            {pdfMeta && (
              <p className="text-xs text-gray-500 mb-1 shrink-0">
                {selected?.name}
                {selected?.code ? ` · ${selected.code}` : ""}
                {pdfMeta.page
                  ? ` · Seite ${pdfMeta.page}${pdfMeta.source === "bulk_mapping" ? " (Bulk-Mapping)" : ""}`
                  : ""}
              </p>
            )}
            <div className="flex-1 min-h-0 border border-gray-200 rounded bg-gray-50 flex flex-col">
              {!selectedId ? (
                <p className="text-sm text-gray-500 p-4">
                  Keine Lehrkraft gewählt.
                </p>
              ) : error && !context ? (
                <p className="text-sm text-gray-500 p-4">PDF nicht geladen.</p>
              ) : pdfUrl ? (
                <EvidencePdfPane
                  url={pdfUrl}
                  view={pdfView}
                  onViewChange={(next) => {
                    if (next.scrollLeft != null || next.scrollTop != null) {
                      queueScroll({
                        pdfScrollLeft:
                          next.scrollLeft ?? personView.pdfScrollLeft,
                        pdfScrollTop: next.scrollTop ?? personView.pdfScrollTop,
                      });
                    }
                    if (next.scale != null)
                      updatePersonView({ pdfScale: next.scale });
                  }}
                />
              ) : pdfLoading || !findingsReady || (!context && !error) ? (
                <p className="text-sm text-gray-500 p-4">Lade PDF…</p>
              ) : pdfError ? (
                <p className="text-sm text-red-600 p-4">{pdfError}</p>
              ) : !pdfMeta ? (
                <p className="text-sm text-gray-500 p-4">
                  Kein PDF-Stundenplan vorhanden (weder Import-Mapping noch
                  Upload der Kategorie „Stundenplan“).
                </p>
              ) : (
                <p className="text-sm text-gray-500 p-4">
                  Seite unbekannt — das Sammel-PDF wird nicht geöffnet.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {rebind.panel}
      {pdfLarger && pdfUrl && selectedId && (
        <PdfTimetableModal
          teacherId={selectedId}
          batchId={batchId}
          existingUrl={pdfUrl}
          pdfMeta={pdfMeta}
          onClose={() => setPdfLarger(false)}
        />
      )}
    </div>
  );
}
